import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Upload, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Card, PageHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import PromptDialog from '@/components/ui/PromptDialog';
import UploadDialog, { type Preset } from '@/components/documents/UploadDialog';
import { Checklist, DocCard, ResultCard } from '@/components/documents/DocParts';
import { qk } from '@/lib/queries';
import {
  deleteDoc, docText, errText, fetchCatalog, fetchVault, markChecked, openDoc, recheckDoc, saveProfile,
  type Doc, type Profile, type Slot, type Surface,
} from '@/lib/documents';

const PLATFORM_LABEL: Record<string, string> = { meta: 'Meta (Instagram, Facebook)', tiktok: 'TikTok', snapchat: 'Snapchat', google: 'Google Ads', x: 'X' };
const REG_LABEL: Record<string, string> = { dha: 'DHA (Dubai)', doh: 'DOH (Abu Dhabi)', mohap: 'MOHAP (northern emirates)', dhcc: 'DHCC (Dubai Healthcare City)' };

/** A clinic's documents vault (Aziz 2026-10-22): what we need to start, what we have, and
 *  whether each file says what it should. Opened from the client page; documents.read. */
export default function ClientDocumentsPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const surface: Surface = useMemo(() => ({ kind: 'staff', clientId: id }), [id]);
  const catalog = useQuery({ queryKey: qk.docCatalog, queryFn: fetchCatalog, staleTime: 3600_000 });
  const vault = useQuery({
    queryKey: qk.clientDocuments(id), queryFn: () => fetchVault(id),
    // while a document is being read, look again every few seconds
    refetchInterval: (q) => (q.state.data?.documents.some((d) => d.ocr_status === 'pending' || d.ocr_status === 'running') ? 3000 : false),
  });
  const [dialog, setDialog] = useState<{ preset?: Preset | null; replace?: Doc | null } | null>(null);
  const [del, setDel] = useState<Doc | null>(null);
  const [check, setCheck] = useState<Doc | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: qk.clientDocuments(id) });

  if (vault.isLoading || catalog.isLoading) return <p className="text-sm text-muted">Loading the documents...</p>;
  if (vault.isError) {
    const st = (vault.error as { response?: { status?: number } })?.response?.status;
    return <p className="text-sm text-red-400">{st === 403 ? 'The documents vault needs the "Client documents: view" permission. Ask the owner or a manager.' : 'Could not load the documents.'}</p>;
  }
  const v = vault.data!;
  const cat = catalog.data!;
  const groups = Object.entries(cat.doc_types)
    .map(([k, l]) => ({ key: k, label: l, docs: v.documents.filter((d) => d.doc_type === k) }))
    .filter((g) => g.docs.length);

  const fromSlot = (s: Slot) => setDialog({ preset: { catalogKey: s.suggest, docType: s.doc_type, authority: s.authority } });

  return (
    <div>
      <Link to={`/clients/${id}`} className="inline-flex items-center gap-1 text-xs text-muted hover:text-accent-400 mb-3">
        <ArrowLeft size={12} /> Back to {v.client_name}
      </Link>
      <PageHeader
        title={`${v.client_name}: documents`}
        subtitle="Licences, certificates, permits, IDs and contracts in one place. Each upload is read and checked automatically."
        actions={v.can_write ? <Button icon={<Upload size={14} />} onClick={() => setDialog({})} data-testid="doc-upload-open">Upload a document</Button> : undefined}
      />

      <Card className="mb-4"><Checklist slots={v.checklist} canUpload={v.can_write} onUpload={fromSlot} /></Card>

      <ProfileCard clientId={id} profile={v.profile} canWrite={v.can_write} onSaved={(nv) => qc.setQueryData(qk.clientDocuments(id), nv)} />

      <div className="grid gap-4" data-testid="doc-list">
        {groups.length === 0 && <Card><p className="text-sm text-muted">No documents yet.</p></Card>}
        {groups.map((g) => (
          <Card key={g.key}>
            <div className="flex items-baseline gap-2 mb-3">
              <h2 className="text-base font-semibold text-white">{g.label.en}</h2>
              <span className="text-xs text-muted" dir="rtl">{g.label.ar}</span>
            </div>
            <div className="grid gap-3">
              {g.docs.map((d) => (
                <DocCard key={d.id} doc={d}
                  onOpen={(docId) => openDoc(surface, docId).catch((e) => toast.error(errText(e, 'Could not open the file')))}
                  onReplace={v.can_write ? () => setDialog({ replace: d }) : undefined}
                  onDelete={v.can_manage ? () => setDel(d) : undefined}
                  result={<ResultCard doc={d}
                    onText={async () => (await docText(id, d.id)).text}
                    onChecked={v.can_manage ? () => setCheck(d) : undefined}
                    onRecheck={v.can_write ? () => recheckDoc(id, d.id).then(refresh).catch((e) => toast.error(errText(e))) : undefined} />} />
              ))}
            </div>
          </Card>
        ))}
      </div>

      <UploadDialog surface={surface} catalog={cat} open={!!dialog} preset={dialog?.preset} replace={dialog?.replace}
        onClose={() => setDialog(null)} onDone={() => { setDialog(null); refresh(); }} />
      <ConfirmDialog open={!!del} title="Delete this document?" confirmLabel="Delete"
        message={del ? `${del.title} leaves the list. It is kept in the records and can be restored by an admin.` : ''}
        onCancel={() => setDel(null)}
        onConfirm={async () => {
          try { await deleteDoc(id, del!.id); toast.success('Document deleted'); refresh(); } catch (e) { toast.error(errText(e)); }
          setDel(null);
        }} />
      <PromptDialog open={!!check} title="Checked by a person" confirmLabel="Mark as checked"
        message="You looked at the document yourself and it is right. A short note helps the next person (optional)."
        placeholder="e.g. Old trading name, confirmed with the clinic" multiline
        onCancel={() => setCheck(null)}
        onConfirm={async (note) => {
          try { await markChecked(id, check!.id, note); toast.success('Marked as checked'); refresh(); } catch (e) { toast.error(errText(e)); }
          setCheck(null);
        }} />
    </div>
  );
}

/** Emirate, regulator and ad platforms decide what the checklist asks for. */
function ProfileCard({ clientId, profile, canWrite, onSaved }: {
  clientId: string; profile: Profile; canWrite: boolean; onSaved: (v: Awaited<ReturnType<typeof saveProfile>>) => void;
}) {
  const [p, setP] = useState(profile);
  const [saving, setSaving] = useState(false);
  useEffect(() => setP(profile), [profile]);
  const dirty = JSON.stringify({ ...p, inferred: [] }) !== JSON.stringify({ ...profile, inferred: [] });
  const guessed = (k: string) => (profile.inferred.includes(k) ? ' (guessed, save to confirm)' : '');
  const save = async () => {
    setSaving(true);
    try {
      const { inferred: _i, ...body } = p; // eslint-disable-line @typescript-eslint/no-unused-vars
      onSaved(await saveProfile(clientId, body)); toast.success('Saved');
    } catch (e) { toast.error(errText(e)); } finally { setSaving(false); }
  };
  return (
    <details className="mb-4 rounded-card border border-slate-800 bg-bg-card/85 p-4" data-testid="doc-profile">
      <summary className="cursor-pointer text-sm text-muted-strong">What the checklist asks for: emirate, regulator, ad platforms, legal name</summary>
      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        <Select label={`Emirate${guessed('emirate')}`} value={p.emirate} disabled={!canWrite} onChange={(x) => setP({ ...p, emirate: x })}
          options={[{ value: '', label: 'Not set' }, ...['dubai', 'abu_dhabi', 'sharjah', 'ajman', 'rak', 'uaq', 'fujairah'].map((k) => ({ value: k, label: k.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase()).replace('Rak', 'Ras Al Khaimah').replace('Uaq', 'Umm Al Quwain') }))]} />
        <Select label={`Health regulator${guessed('regulator')}`} value={p.regulator} disabled={!canWrite} onChange={(x) => setP({ ...p, regulator: x })}
          options={[{ value: '', label: 'Not set' }, ...Object.entries(REG_LABEL).map(([k, l]) => ({ value: k, label: l }))]} />
        <Input label="Legal name (English)" value={p.legal_name_en} disabled={!canWrite} onChange={(e) => setP({ ...p, legal_name_en: e.target.value })}
          hint="As printed on the trade licence; the check compares it" />
        <Input label="Legal name (Arabic)" dir="rtl" value={p.legal_name_ar} disabled={!canWrite} onChange={(e) => setP({ ...p, legal_name_ar: e.target.value })} />
        <label className="flex items-center gap-2 text-sm text-muted-strong">
          <input type="checkbox" checked={p.free_zone} disabled={!canWrite} onChange={(e) => setP({ ...p, free_zone: e.target.checked })} /> Licensed in a free zone
        </label>
        <div className="sm:col-span-2">
          <div className="text-xs text-muted mb-1">Ad platforms this clinic advertises on{guessed('ad_platforms') && ' (read from its posting channels, save to confirm)'}</div>
          <div className="flex flex-wrap gap-3">
            {Object.entries(PLATFORM_LABEL).map(([k, l]) => (
              <label key={k} className="flex items-center gap-1.5 text-sm text-muted-strong">
                <input type="checkbox" disabled={!canWrite} checked={p.ad_platforms.includes(k)}
                  onChange={(e) => setP({ ...p, ad_platforms: e.target.checked ? [...p.ad_platforms, k] : p.ad_platforms.filter((x) => x !== k) })} /> {l}
              </label>
            ))}
          </div>
        </div>
      </div>
      {canWrite && <div className="mt-3 flex justify-end"><Button size="sm" icon={<Save size={12} />} loading={saving} disabled={!dirty && !profile.inferred.length} onClick={save}>Save</Button></div>}
    </details>
  );
}
